import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import type { Me, SystemInfo, UserSummary } from '../api/types';
import { mergeUser } from '../utils/users';
import { useSession } from './SessionContext';

interface AppContextValue {
  me: Me;
  users: UserSummary[];
  systems: SystemInfo[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  upsertUser: (user: UserSummary) => void;
  systemById: (id: string) => SystemInfo | undefined;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [systems, setSystems] = useState<SystemInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [nextUsers, nextSystems] = await Promise.all([api.users(), api.systems()]);
      setUsers(nextUsers);
      setSystems(nextSystems);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  const upsertUser = useCallback((user: UserSummary) => {
    setUsers((previous) => mergeUser(previous, user));
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AppContextValue>(
    () => ({
      me,
      users,
      systems,
      loading,
      error,
      refresh,
      upsertUser,
      systemById: (id: string) => systems.find((system) => system.id === id),
    }),
    [me, users, systems, loading, error, refresh, upsertUser],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used inside AppProvider');
  }
  return context;
}
